/**
 * API Client
 * Centralized HTTP client for all API communication
 * Uses Axios with interceptors for error handling and request/response transformation
 */

import { ApiError } from '@/domain/types';
import type { HttpRequestConfig, IHttpClient } from '@/application/core';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { apiConfig } from './apiConfig';

/**
 * API Client configuration
 */
export interface ApiClientConfig {
  /** Base URL for API requests */
  baseURL?: string;

  /** Request timeout in milliseconds */
  timeout?: number;

  /** Additional headers */
  headers?: Record<string, string>;
}

/**
 * Map framework-agnostic HttpRequestConfig to AxiosRequestConfig
 */
function toAxiosConfig(config?: HttpRequestConfig): AxiosRequestConfig | undefined {
  if (!config) return undefined;
  return {
    headers: config.headers,
    params: config.params,
    timeout: config.timeout,
    signal: config.signal,
    responseType: config.responseType,
  };
}

/**
 * Error response data shape from the server
 */
interface ErrorResponseData {
  message?: string;
  code?: string;
  errors?: Record<string, string[]>;
}

/**
 * API Client class
 * Provides a centralized interface for making HTTP requests
 */
export class ApiClient implements IHttpClient {
  private instance: AxiosInstance;

  /**
   * Pages that don't require authentication.
   * The interceptor will NOT redirect to /login on these pages.
   */
  private static readonly ANONYMOUS_PAGES: string[] = [
    '/developer-registration',
    '/developer-registration-payment-complete',
    '/developer-registration-success',
    '/developer-registration-error',
    '/demo-registration',
    '/demo-registration-success',
    '/demo-registration-error',
    '/change-log'
  ];

  constructor(config: ApiClientConfig = {}) {
    this.instance = axios.create({
      baseURL: config.baseURL || '',
      timeout: config.timeout || 300000, // 5 minutes
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });

    this.setupInterceptors();
  }

  /**
   * Check if a response URL indicates a redirect to the login page.
   * When session/cookie expires, the server returns 302 -> /login.
   * The browser auto-follows the redirect, so Axios receives 200 + HTML.
   * We detect this by checking the final responseURL.
   */
  private isLoginRedirect(request: XMLHttpRequest | undefined): boolean {
    const responseURL = request?.responseURL;
    if (!responseURL) return false;
    try {
      return new URL(responseURL).pathname.toLowerCase().startsWith('/login');
    } catch {
      return responseURL.toLowerCase().includes('/login');
    }
  }

  /**
   * Detect an XHR to the API surface that was redirected away from it. An "/api/..." call that
   * resolves at a non-"/api" path (e.g. /login or /dashboard) means the server bounced an
   * unauthenticated request through its login-redirect chain — i.e. the session expired. The chain
   * can land on /dashboard rather than /login, which isLoginRedirect (which only checks for /login)
   * misses — leaving the caller to parse an HTML page as JSON and crash on the missing data.
   */
  private wasApiCallRedirectedAway(
    requestedUrl: string | undefined,
    request: XMLHttpRequest | undefined
  ): boolean {
    const responseURL = request?.responseURL;
    if (!requestedUrl || !responseURL) return false;
    if (!requestedUrl.toLowerCase().includes('/api/')) return false;
    try {
      const finalPath = new URL(responseURL, window.location.origin).pathname.toLowerCase();
      return !finalPath.startsWith('/api/');
    } catch {
      return false;
    }
  }

  /**
   * Check if the current page is an anonymous (public) page.
   * Anonymous pages should not trigger login redirects.
   */
  private isAnonymousPage(): boolean {
    const currentPath = window.location.pathname.toLowerCase();
    return ApiClient.ANONYMOUS_PAGES.some((p) => currentPath.startsWith(p));
  }

  /**
   * Redirect to the login page with ReturnUrl so user returns after re-login.
   */
  private redirectToLogin(): void {
    const returnUrl = window.location.pathname + window.location.search;
    window.location.href = `/login?ReturnUrl=${encodeURIComponent(returnUrl)}`;
  }

  /**
   * Setup response interceptor to detect session expiry and API errors
   */
  private setupInterceptors(): void {
    this.instance.interceptors.response.use(
      (response) => {
        // Skip redirect for anonymous pages (e.g., developer registration)
        if (this.isAnonymousPage()) return response;

        // Detect session expiry: browser followed 302 -> /login (or the redirect chain bounced an
        // /api call onto a non-API HTML page like /dashboard), and we got 200 + HTML.
        if (
          this.isLoginRedirect(response.request) ||
          this.wasApiCallRedirectedAway(response.config?.url, response.request)
        ) {
          this.redirectToLogin();
          return Promise.reject(new Error('Session expired'));
        }
        return response;
      },
      (error: AxiosError) => {
        // Skip redirect for anonymous pages
        if (!this.isAnonymousPage()) {
          // Detect session expiry in error responses
          if (this.isLoginRedirect(error.request as XMLHttpRequest | undefined)) {
            this.redirectToLogin();
            return Promise.reject(new Error('Session expired'));
          }

          // Handle 401 Unauthorized
          if (error.response?.status === 401) {
            this.redirectToLogin();
          }
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * Handle and transform Axios errors to ApiError
   */
  private handleError(error: AxiosError): ApiError {
    if (error.response) {
      // Server responded with error status
      const data = error.response.data as ErrorResponseData | undefined;
      const { status } = error.response;

      return {
        message: data?.message || error.message || 'An error occurred',
        code: data?.code,
        status,
        errors: data?.errors,
      };
    } else if (error.code === 'ECONNABORTED') {
      // Request timed out
      return {
        message: 'Request timed out - the server is taking too long to respond',
        status: 0,
      };
    } else if (error.request) {
      // Request made but no response received
      return {
        message: 'Network error - please check your connection',
        status: 0,
      };
    } else {
      // Error setting up request
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0,
      };
    }
  }

  /**
   * GET request
   */
  async get<T = unknown>(url: string, config?: HttpRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(url, toAxiosConfig(config));
    return response.data;
  }

  /**
   * POST request
   */
  async post<T = unknown>(url: string, data?: unknown, config?: HttpRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(url, data, toAxiosConfig(config));
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T = unknown>(url: string, data?: unknown, config?: HttpRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(url, data, toAxiosConfig(config));
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T = unknown>(url: string, data?: unknown, config?: HttpRequestConfig): Promise<T> {
    const response = await this.instance.patch<T>(url, data, toAxiosConfig(config));
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = unknown>(url: string, config?: HttpRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(url, toAxiosConfig(config));
    return response.data;
  }

  /** @internal Get Axios instance for advanced usage */
  getInstance(): AxiosInstance {
    return this.instance;
  }
}

// Export singleton instance
export const apiClient = new ApiClient(apiConfig);
